import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, Form, Alert, Spinner } from "react-bootstrap";
import FlipClassService, {
  SuggestedGroup,
  SuggestedMember,
  SessionGroupRecord,
} from "../../_services/FlipClassService";
import "./GroupFormationModal.css";

interface GroupFormationModalProps {
  stepId: number;
  batchId: number;
  isOpen: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

type WorkingGroup = {
  name: string;
  members: SuggestedMember[];
};

const DEFAULT_GROUP_SIZE = 4;
const MIN_GROUP_SIZE = 2;
const MAX_GROUP_SIZE = 8;

function avg(members: SuggestedMember[]): number | null {
  const vals = members.map((m) => m.preScore).filter((n): n is number => n != null);
  if (vals.length === 0) return null;
  return Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 100) / 100;
}

const GroupFormationModal: React.FC<GroupFormationModalProps> = ({
  stepId,
  batchId,
  isOpen,
  onClose,
  onSaved,
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const [groupSize, setGroupSize] = useState(DEFAULT_GROUP_SIZE);
  const [workingGroups, setWorkingGroups] = useState<WorkingGroup[]>([]);
  const [strategy, setStrategy] = useState<"balanced_prequiz" | "random_balanced" | null>(null);
  const [rosterCount, setRosterCount] = useState(0);
  const [existing, setExisting] = useState<SessionGroupRecord[]>([]);

  useEffect(() => {
    if (!isOpen) {
      setWorkingGroups([]);
      setStrategy(null);
      setError(null);
      setInfo(null);
      return;
    }
    loadExisting();
  }, [isOpen, stepId, batchId]);

  const loadExisting = async () => {
    try {
      setLoading(true);
      const resp = await FlipClassService.listGroups(stepId, batchId);
      if (resp.success) {
        setExisting(resp.data.groups || []);
        if ((resp.data.groups || []).length > 0) {
          const reconstructed: WorkingGroup[] = resp.data.groups.map((g) => ({
            name: g.name,
            members: g.members.map((m) => ({
              userId: m.userId,
              enrollmentId: m.enrollmentId ?? 0,
              name: m.name,
              preScore: null,
            })),
          }));
          setWorkingGroups(reconstructed);
          setStrategy(null);
          setInfo(`Loaded ${resp.data.groups.length} existing groups from today.`);
        }
      }
    } catch (e: any) {
      setError(e?.message || "Failed to load existing groups");
    } finally {
      setLoading(false);
    }
  };

  const runSuggest = async () => {
    try {
      setLoading(true);
      setError(null);
      setInfo(null);
      const resp = await FlipClassService.suggestGroups(stepId, batchId, { groupSize });
      if (!resp.success) {
        setError("Could not suggest groups");
        return;
      }
      setRosterCount(resp.data.rosterCount);
      setStrategy(resp.data.strategy);
      setWorkingGroups(
        resp.data.groups.map((g) => ({ name: g.name, members: g.members }))
      );
      if (resp.data.rosterCount === 0) {
        setError("No students found in this batch for this step.");
      }
    } catch (e: any) {
      setError(e?.message || "Failed to suggest groups");
    } finally {
      setLoading(false);
    }
  };

  const moveMember = (fromIdx: number, userId: number, toIdx: number) => {
    if (fromIdx === toIdx) return;
    setWorkingGroups((prev) => {
      const next = prev.map((g) => ({ ...g, members: [...g.members] }));
      const fromGroup = next[fromIdx];
      const toGroup = next[toIdx];
      if (!fromGroup || !toGroup) return prev;
      const memberIndex = fromGroup.members.findIndex((m) => m.userId === userId);
      if (memberIndex === -1) return prev;
      const [member] = fromGroup.members.splice(memberIndex, 1);
      if (member) toGroup.members.push(member);
      return next;
    });
  };

  const renameGroup = (idx: number, newName: string) => {
    setWorkingGroups((prev) =>
      prev.map((g, i) => (i === idx ? { ...g, name: newName.slice(0, 64) } : g))
    );
  };

  const addEmptyGroup = () => {
    setWorkingGroups((prev) => {
      return [...prev, { name: `Group ${prev.length + 1}`, members: [] }];
    });
  };

  const removeGroup = (idx: number) => {
    setWorkingGroups((prev) => {
      const removed = prev[idx];
      const next = prev
        .filter((_, i) => i !== idx)
        .map((g) => ({ ...g, members: [...g.members] }));
      if (removed && removed.members.length > 0 && next.length > 0) {
        const target = next[0]!;
        const existingIds = new Set(target.members.map((m) => m.userId));
        for (const m of removed.members) {
          if (!existingIds.has(m.userId)) {
            target.members.push(m);
            existingIds.add(m.userId);
          }
        }
      }
      return next;
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      const payload = workingGroups
        .filter((g) => g.members.length > 0)
        .map((g) => ({
          name: g.name,
          members: g.members.map((m) => ({
            userId: m.userId,
            enrollmentId: m.enrollmentId || undefined,
          })),
        }));
      if (payload.length === 0) {
        setError("Form at least one non-empty group before saving.");
        setSaving(false);
        return;
      }
      const resp = await FlipClassService.createGroups(stepId, batchId, payload);
      if (resp.success) {
        setInfo(`Saved ${resp.data.groups.length} groups.`);
        if (onSaved) onSaved();
        setTimeout(() => onClose(), 600);
      }
    } catch (e: any) {
      setError(e?.message || "Failed to save groups");
    } finally {
      setSaving(false);
    }
  };

  const totalAssigned = useMemo(
    () => workingGroups.reduce((sum, g) => sum + g.members.length, 0),
    [workingGroups]
  );

  return (
    <Modal
      show={isOpen}
      onHide={onClose}
      size="xl"
      dialogClassName="group-formation-modal-dialog"
      backdrop="static"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          Form Groups for Today's Class
          <div className="gfm-subtitle">
            Foundation for AI case discussions — pair students into discussion groups
          </div>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {info && <Alert variant="success">{info}</Alert>}

        <div className="gfm-controls">
          <div className="gfm-control-row">
            <Form.Label className="gfm-control-label">Target group size</Form.Label>
            <Form.Control
              type="number"
              min={MIN_GROUP_SIZE}
              max={MAX_GROUP_SIZE}
              value={groupSize}
              onChange={(e) => setGroupSize(Number(e.target.value) || DEFAULT_GROUP_SIZE)}
              className="gfm-group-size"
            />
            <Button
              variant="primary"
              onClick={runSuggest}
              disabled={loading}
              className="gfm-suggest-btn"
            >
              {loading ? (
                <>
                  <Spinner size="sm" animation="border" /> Suggesting…
                </>
              ) : workingGroups.length > 0 ? (
                "Re-suggest Groups"
              ) : (
                "Suggest Groups"
              )}
            </Button>
            {workingGroups.length > 0 && (
              <Button variant="secondary" onClick={addEmptyGroup} className="gfm-add-btn">
                + Add empty group
              </Button>
            )}
          </div>

          {strategy && (
            <div className="gfm-strategy-banner">
              {strategy === "balanced_prequiz" ? (
                <>
                  <strong>Balanced by pre-quiz score.</strong> Snake-drafted across {workingGroups.length}{" "}
                  groups from {rosterCount} students so each group has a mix of strong & weak performers.
                </>
              ) : (
                <>
                  <strong>Random balanced.</strong> No pre-quiz scores available for this step — students
                  distributed randomly across {workingGroups.length} groups.
                </>
              )}
            </div>
          )}
        </div>

        {workingGroups.length === 0 && !loading && (
          <div className="gfm-empty">
            <div className="gfm-empty-icon">👥</div>
            <h4>Click "Suggest Groups" to start</h4>
            <p>
              We'll snake-draft students by pre-quiz performance so every group has a mix of abilities.
              You can adjust members manually before saving.
            </p>
          </div>
        )}

        {workingGroups.length > 0 && (
          <>
            <div className="gfm-summary">
              {workingGroups.length} groups · {totalAssigned} students assigned
            </div>

            <div className="gfm-group-grid">
              {workingGroups.map((g, gIdx) => {
                const groupAvg = avg(g.members);
                return (
                  <div key={gIdx} className="gfm-group-card">
                    <div className="gfm-group-card-header">
                      <input
                        type="text"
                        value={g.name}
                        onChange={(e) => renameGroup(gIdx, e.target.value)}
                        className="gfm-group-name-input"
                        maxLength={64}
                      />
                      <button
                        type="button"
                        className="gfm-group-remove"
                        title="Remove group"
                        onClick={() => removeGroup(gIdx)}
                      >
                        ×
                      </button>
                    </div>
                    <div className="gfm-group-meta">
                      <span className="gfm-meta-pill">{g.members.length} members</span>
                      {groupAvg != null && (
                        <span className="gfm-meta-pill gfm-meta-score">
                          Pre-quiz avg: {groupAvg.toFixed(1)}
                        </span>
                      )}
                    </div>
                    <div className="gfm-member-list">
                      {g.members.length === 0 && (
                        <div className="gfm-no-members">No students assigned</div>
                      )}
                      {g.members.map((m) => (
                        <div key={m.userId} className="gfm-member-row">
                          <div className="gfm-member-info">
                            <span className="gfm-member-name">{m.name}</span>
                            {m.preScore != null && (
                              <span className="gfm-member-score">
                                {m.preScore.toFixed(0)}%
                              </span>
                            )}
                          </div>
                          <select
                            className="gfm-member-move"
                            value=""
                            onChange={(e) => {
                              const toIdx = Number(e.target.value);
                              if (!Number.isNaN(toIdx)) moveMember(gIdx, m.userId, toIdx);
                            }}
                          >
                            <option value="" disabled>
                              Move…
                            </option>
                            {workingGroups.map((other, oIdx) =>
                              oIdx === gIdx ? null : (
                                <option key={oIdx} value={oIdx}>
                                  → {other.name}
                                </option>
                              )
                            )}
                          </select>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={saving || workingGroups.length === 0 || totalAssigned === 0}
        >
          {saving ? (
            <>
              <Spinner size="sm" animation="border" /> Saving…
            </>
          ) : (
            "Save Groups"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default GroupFormationModal;
